import React, { useState, useEffect, useMemo, useCallback, useReducer } from "react";
import { useParams, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { BookingPageSkeleton } from "@/components/booking/shared/LoadingStates";
import { debugLog, isDebugModeEnabled } from "@/lib/debug-utils";
import { Helmet } from "react-helmet-async";
import { TemplateLoader, isValidTemplate } from "@/components/booking/templates";
import type { TemplateType } from "@/components/booking/templates";
import { useAuth } from "@/lib/auth";
import { HostTroubleshootBanner } from "@/components/booking/HostTroubleshootBanner";
import { BookingPage } from "@/components/booking/BookingPage";
import { trackEvent } from "@/lib/analytics/track-event";
import { BookingProvider, type ExistingBooking, type BookingMode } from "@/contexts/BookingContext";
import { generateBookingSEOData, generateBookingStructuredData } from "@/lib/seo-utils";

// Consolidated state interface
interface PageState {
  meetingLink: any;
  existingBooking: ExistingBooking | null;
  loading: boolean;
  error: string | null;
  isDebugMode: boolean;
  troubleshootMode: boolean;
  isHost: boolean;
  hasTrackedView: boolean;
  bookingMode: BookingMode;
}

// State reducer for better state management
type PageAction = 
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_MEETING_LINK'; payload: any }
  | { type: 'SET_EXISTING_BOOKING'; payload: ExistingBooking | null }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_DEBUG_MODE'; payload: boolean }
  | { type: 'SET_TROUBLESHOOT_MODE'; payload: boolean }
  | { type: 'SET_IS_HOST'; payload: boolean }
  | { type: 'SET_HAS_TRACKED_VIEW'; payload: boolean }
  | { type: 'SET_BOOKING_MODE'; payload: BookingMode };

const pageReducer = (state: PageState, action: PageAction): PageState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_MEETING_LINK':
      return { ...state, meetingLink: action.payload, loading: false };
    case 'SET_EXISTING_BOOKING':
      return { ...state, existingBooking: action.payload };
    case 'SET_ERROR':
      // Only set loading to false if there's an actual error, not when clearing errors
      if (action.payload) {
        return { ...state, error: action.payload, loading: false };
      } else {
        return { ...state, error: action.payload };
      }
    case 'SET_DEBUG_MODE':
      return { ...state, isDebugMode: action.payload };
    case 'SET_TROUBLESHOOT_MODE':
      return { ...state, troubleshootMode: action.payload };
    case 'SET_IS_HOST':
      return { ...state, isHost: action.payload };
    case 'SET_HAS_TRACKED_VIEW':
      return { ...state, hasTrackedView: action.payload };
    case 'SET_BOOKING_MODE':
      return { ...state, bookingMode: action.payload };
    default:
      return state;
  }
};

// Helper function to parse URL search params - memoized to prevent re-renders
const useQueryParams = () => {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
};

// Memoized URL parameter parser to prevent unnecessary re-renders
const useUrlParams = (queryParams: URLSearchParams) => {
  return React.useMemo(() => ({
    month: queryParams.get('month'),
    date: queryParams.get('date'),
    slot: queryParams.get('slot'),
    duration: queryParams.get('duration') ? parseInt(queryParams.get('duration')!) : undefined,
    name: queryParams.get('name'),
    email: queryParams.get('email'),
    company: queryParams.get('company'),
    message: queryParams.get('message')
  }), [queryParams]);
};

const ExternalBookingPage = React.memo(() => {
  const { slug, businessSlug, bookingId } = useParams<{ slug?: string; businessSlug?: string; bookingId?: string }>();
  const queryParams = useQueryParams();
  const { user } = useAuth();
  
  // Determine booking mode from URL
  const actionParam = queryParams.get('action');
  const isBookingManagement = !!bookingId;
  const bookingMode: BookingMode = isBookingManagement 
    ? (actionParam === 'reschedule' ? 'reschedule' : actionParam === 'cancel' ? 'cancel' : 'reschedule')
    : 'new';

  // Use reducer for better state management
  const [pageState, dispatch] = useReducer(pageReducer, {
    meetingLink: null,
    existingBooking: null,
    loading: true,
    error: null,
    isDebugMode: false,
    troubleshootMode: false,
    isHost: false,
    hasTrackedView: false,
    bookingMode
  });
  
  // Memoize URL parameters to prevent unnecessary re-renders
  const urlParams = useUrlParams(queryParams);
  
  // Memoize business data to prevent recalculation
  const businessData = useMemo(() => ({
    businessName: pageState.meetingLink?.profiles?.business_name || "ChronoCal User",
    businessSlugValue: pageState.meetingLink?.profiles?.business_slug || "chronocal-user",
    canonicalUrl: `${window.location.origin}${window.location.pathname}`
  }), [pageState.meetingLink?.profiles?.business_name, pageState.meetingLink?.profiles?.business_slug]);
  
  // Generate SEO data using utility functions - memoized to prevent recalculation
  const seoData = useMemo(() => {
    if (!pageState.meetingLink) return null;
    
    return generateBookingSEOData(
      pageState.meetingLink,
      businessData.businessName,
      businessData.businessSlugValue,
      pageState.bookingMode,
      businessData.canonicalUrl
    );
  }, [pageState.meetingLink, businessData.businessName, businessData.businessSlugValue, pageState.bookingMode, businessData.canonicalUrl]);
  
  // Generate structured data - memoized to prevent recalculation
  const schemaData = useMemo(() => {
    return seoData ? generateBookingStructuredData(seoData) : null;
  }, [seoData]);
  
  // Stable debug meeting link - memoized to prevent recreation
  const debugMeetingLink = useMemo(() => ({
    id: 'debug',
    name: 'Debug Meeting',
    duration: urlParams.duration || 30,
    energy_preference: 'power',
    slug: 'debug',
    description: 'This is an example meeting link for testing the booking flow.',
    user_id: 'debug',
    active: true,
    max_per_day: 3,
    template: 'modern', // Always use modern template
    profiles: {
      business_name: 'Debug Business',
      business_logo_url: null
    }
  }), [urlParams.duration]);

  // Debounced track view function - optimized dependencies
  const trackViewEvent = useCallback(async (meetingLinkId: string) => {
    if (pageState.hasTrackedView || pageState.isHost) return;
    
    try {
      await trackEvent({
        name: "booking_link_viewed",
        properties: {
          meetingLinkId,
          source: "direct",
          businessSlug: businessSlug || null
        }
      });
      
      dispatch({ type: 'SET_HAS_TRACKED_VIEW', payload: true });
    } catch (e) {
      console.error("Error tracking view event:", e);
    }
  }, [pageState.hasTrackedView, pageState.isHost, businessSlug]);

  // Load existing booking data for reschedule/cancel mode
  useEffect(() => {
    if (!isBookingManagement || !bookingId) {
      return;
    }

    let isCancelled = false;

    const fetchExistingBooking = async () => {
      try {
        dispatch({ type: 'SET_LOADING', payload: true });
        dispatch({ type: 'SET_ERROR', payload: null });

        // Fetch booking details with meeting link info
        const { data: bookingData, error: bookingError } = await supabase
          .from('bookings')
          .select(`
            *,
            meeting_links!bookings_meeting_link_id_fkey(
              id, slug, user_id, name, duration, energy_preference, 
              description, active, max_per_day, template,
              deactivation_reason, max_advance_booking_days,
              background_color, custom_font, custom_logo_url, 
              background_image_url, left_column_background_url,
              logo_border_radius, logo_size, is_logo_rounded,
              title_text_color, description_text_color, duration_text_color,
              show_all_slots
            )
          `)
          .eq('id', bookingId)
          .single();

        if (isCancelled) return;

        if (bookingError) {
          // Provide more user-friendly error messages
          let errorMessage = 'Error loading booking';
          if (bookingError.message.includes('Cannot coerce the result to a single JSON object')) {
            errorMessage = 'Booking not found or no longer available';
          } else if (bookingError.message.includes('permission denied') || bookingError.message.includes('insufficient_privilege')) {
            errorMessage = 'Access denied. Please check your booking link.';
          } else if (bookingError.message.includes('not found')) {
            errorMessage = 'Booking not found';
          } else {
            errorMessage = `Error loading booking: ${bookingError.message}`;
          }
          dispatch({ type: 'SET_ERROR', payload: errorMessage });
          return;
        }

        if (!bookingData) {
          dispatch({ type: 'SET_ERROR', payload: "Booking not found" });
          return;
        }

        if (!bookingData.meeting_links) {
          dispatch({ type: 'SET_ERROR', payload: "Associated meeting link not found" });
          return;
        }

        // Get profile data for the meeting link owner
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('business_name, business_logo_url, business_slug')
          .eq('id', bookingData.meeting_links.user_id)
          .maybeSingle();

        if (isCancelled) return;

        if (profileError) {
          console.error('Error fetching profile:', profileError);
        }

        // Create existing booking object
        const existingBooking: ExistingBooking = {
          id: bookingData.id,
          booking_date: bookingData.booking_date,
          duration: bookingData.meeting_links.duration,
          timezone: bookingData.timezone || 'UTC',
          meeting_name: bookingData.meeting_links.name,
          booker_name: bookingData.booker_name,
          booker_email: bookingData.booker_email,
          booker_company: bookingData.booker_company,
          message: bookingData.message,
          status: bookingData.status,
          meeting_link_id: bookingData.meeting_link_id,
          host_user_id: bookingData.meeting_links.user_id,
          calendar_event_id: bookingData.calendar_event_id
        };

        // Prepare meeting link data
        const meetingLinkData = {
          ...bookingData.meeting_links,
          profiles: profileData || {}
        };

        dispatch({ type: 'SET_EXISTING_BOOKING', payload: existingBooking });
        dispatch({ type: 'SET_MEETING_LINK', payload: meetingLinkData });

      } catch (error) {
        if (!isCancelled) {
          console.error("Unexpected error loading booking:", error);
          dispatch({ type: 'SET_ERROR', payload: `Failed to load booking: ${error instanceof Error ? error.message : 'Unknown error'}` });
        }
      }
    };

    const timeoutId = setTimeout(fetchExistingBooking, 100);

    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [bookingId, isBookingManagement]);

  // Check debug mode only once on mount
  useEffect(() => {
    const savedDebugMode = localStorage.getItem('developmentDebugMode') === 'true';
    const isDebugEnv = process.env.NODE_ENV === 'development';
    const isDebugRoute = slug === 'debug';
    
    const debugMode = savedDebugMode && isDebugEnv || isDebugRoute;
    dispatch({ type: 'SET_DEBUG_MODE', payload: debugMode });
    
    if (isDebugRoute) {
      dispatch({ type: 'SET_MEETING_LINK', payload: debugMeetingLink });
    }
  }, [slug, debugMeetingLink]);

  useEffect(() => {
    if (slug === 'debug') {
      return;
    }

    // Skip if we're in booking management mode (data loaded elsewhere)
    if (isBookingManagement) {
      return;
    }

    if (!slug) {
      dispatch({ type: 'SET_ERROR', payload: 'No meeting link specified' });
      return;
    }

    let isCancelled = false;
    
    const fetchMeetingLink = async () => {
      try {
        dispatch({ type: 'SET_LOADING', payload: true });
        dispatch({ type: 'SET_ERROR', payload: null });
        
        // Fetch meeting link data (include deactivated links to check deactivation reason)
        // Filter by both business_slug and slug to ensure uniqueness
        const query = supabase
          .from('meeting_links')
          .select('*')
          .eq('slug', slug);
        
        // Add business_slug filter if provided in the URL
        if (businessSlug) {
          query.eq('business_slug', businessSlug);
        }
        
        const { data: meetingResponse, error: meetingError } = await query.maybeSingle();

        if (isCancelled) return;

        if (meetingError) {
          dispatch({ type: 'SET_ERROR', payload: `Database error: ${meetingError.message}` });
          return;
        }

        if (!meetingResponse) {
          dispatch({ type: 'SET_ERROR', payload: "Meeting link not found" });
          return;
        }

        // Check if meeting link is soft-deleted
        if (meetingResponse.deleted_at) {
          dispatch({ type: 'SET_ERROR', payload: "This meeting link has been removed by the host." });
          return;
        }

        // Check if meeting link is deactivated due to calendar issues
        if (!meetingResponse.active) {
          if (meetingResponse.deactivation_reason === 'calendar_issue') {
            dispatch({ type: 'SET_ERROR', payload: "This meeting link is temporarily unavailable due to a calendar connection issue. The host has been notified and will resolve this shortly." });
            return;
          } else {
            dispatch({ type: 'SET_ERROR', payload: "This meeting link is currently unavailable." });
            return;
          }
        }

        // Fetch profile data for the meeting link owner
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('business_name, business_logo_url, business_slug')
          .eq('id', meetingResponse.user_id)
          .maybeSingle();
            
        if (isCancelled) return;

        if (profileError) {
          console.error('Error fetching profile:', profileError);
        }

        // Verify business slug if provided
        if (businessSlug && profileData?.business_slug && businessSlug !== profileData.business_slug) {
          dispatch({ type: 'SET_ERROR', payload: "Meeting link not found for this business" });
          return;
        }

        // Override duration if provided in URL
        let finalMeetingData = { ...meetingResponse };
        if (urlParams.duration) {
          finalMeetingData.duration = urlParams.duration;
        }

        // Always set template to modern (since it's the only valid template now)
        finalMeetingData.template = 'modern';

        // Combine the data
        const combinedData = {
          ...finalMeetingData,
          profiles: profileData || {}
        };
        
        dispatch({ type: 'SET_MEETING_LINK', payload: combinedData });
        
      } catch (error) {
        if (!isCancelled) {
          console.error("Unexpected error in fetchMeetingLink:", error);
          dispatch({ type: 'SET_ERROR', payload: `Unexpected error: ${error instanceof Error ? error.message : 'Unknown error'}` });
        }
      }
    };

    const timeoutId = setTimeout(fetchMeetingLink, 100);
    
    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [slug, businessSlug, urlParams.duration, isBookingManagement]);

  // Check if current user is the host - consolidated with other user-related logic
  useEffect(() => {
    if (!user || !pageState.meetingLink?.user_id) return;
    
    const userIsHost = user.id === pageState.meetingLink.user_id;
    const storageKey = `troubleshoot_${pageState.meetingLink.id}`;
    const isTroubleshootEnabled = userIsHost ? localStorage.getItem(storageKey) === 'true' : false;
    
    dispatch({ type: 'SET_IS_HOST', payload: userIsHost });
    dispatch({ type: 'SET_TROUBLESHOOT_MODE', payload: isTroubleshootEnabled });
  }, [user?.id, pageState.meetingLink?.user_id, pageState.meetingLink?.id]);

  // Track page view - with proper guards to prevent repeated calls
  useEffect(() => {
    if (pageState.meetingLink?.id && !pageState.hasTrackedView && !pageState.loading && !pageState.isHost) {
      trackViewEvent(pageState.meetingLink.id);
    }
  }, [pageState.meetingLink?.id, pageState.hasTrackedView, pageState.loading, pageState.isHost, trackViewEvent]);

  // Handle troubleshoot mode toggle
  const handleTroubleshootToggle = useCallback((enabled: boolean) => {
    dispatch({ type: 'SET_TROUBLESHOOT_MODE', payload: enabled });
    if (pageState.meetingLink?.id) {
      localStorage.setItem(`troubleshoot_${pageState.meetingLink.id}`, enabled.toString());
    }
  }, [pageState.meetingLink?.id]);

  if (pageState.loading) {
    return (
      <div>
        <Helmet>
          <title>Loading Meeting Details | ChronoCal</title>
          <meta name="description" content="Loading your meeting details. Please wait..." />
        </Helmet>
        <BookingPageSkeleton template="modern" />
      </div>
    );
  }

  // Fixed error condition: only show error when not loading AND (there's an error OR no meeting link)
  if (pageState.error || (!pageState.meetingLink && !pageState.loading)) {
    return (
      <div>
        <Helmet>
          <title>Meeting Not Found | ChronoCal</title>
          <meta name="description" content="We couldn't find the meeting you're looking for." />
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardContent className="py-8 sm:py-12 px-4 sm:px-6">
              <div className="text-center">
                <h1 className="text-lg sm:text-xl font-medium mb-2">Meeting Not Available</h1>
                <p className="text-sm sm:text-base text-muted-foreground">{pageState.error || "Meeting link not found"}</p>
                {process.env.NODE_ENV === 'development' && (
                  <div className="mt-4 p-4 bg-amber-50 rounded-lg text-left">
                    <h3 className="font-semibold text-amber-800 mb-2">Debug Info:</h3>
                    <div className="text-xs text-amber-700 space-y-1">
                      <p><strong>Slug:</strong> {slug}</p>
                      <p><strong>Business Slug:</strong> {businessSlug || 'None'}</p>
                      <p><strong>Error:</strong> {pageState.error}</p>
                      <p><strong>Current Route:</strong> {window.location.pathname}</p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }
  
  // Always use modern template since it's the only valid template now
  const template: TemplateType = 'modern';

  return (
    <BookingProvider 
      initialMode={pageState.bookingMode} 
      initialBooking={pageState.existingBooking}
    >
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>{seoData?.title || 'ChronoCal Booking'}</title>
          <meta name="description" content={seoData?.description || 'Book a meeting with ChronoCal'} />
          <meta name="keywords" content={seoData?.keywords?.join(', ') || 'booking, calendar, meeting'} />
          <link rel="canonical" href={seoData?.canonicalUrl || businessData.canonicalUrl} />
          
          {/* Open Graph / Facebook */}
          <meta property="og:type" content="website" />
          <meta property="og:title" content={seoData?.title || 'ChronoCal Booking'} />
          <meta property="og:description" content={seoData?.description || 'Book a meeting with ChronoCal'} />
          <meta property="og:url" content={seoData?.canonicalUrl || businessData.canonicalUrl} />
          <meta property="og:image" content={seoData?.ogImage || '/icon-512.png'} />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
          <meta property="og:site_name" content="ChronoCal" />
          
          {/* Twitter */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={seoData?.title || 'ChronoCal Booking'} />
          <meta name="twitter:description" content={seoData?.description || 'Book a meeting with ChronoCal'} />
          <meta name="twitter:image" content={seoData?.ogImage || '/icon-512.png'} />
          <meta name="twitter:site" content="@getChronocal" />
          
          {/* Additional SEO meta tags */}
          <meta name="robots" content="index, follow" />
          <meta name="author" content={seoData?.businessName || businessData.businessName} />
          
          {/* Schema.org JSON-LD structured data */}
          {schemaData && (
            <script type="application/ld+json">
              {JSON.stringify(schemaData)}
            </script>
          )}
        </Helmet>

      {/* Host-only troubleshooting banner */}
      {pageState.isHost && pageState.meetingLink.id && (
        <HostTroubleshootBanner
          meetingLinkId={pageState.meetingLink.id}
          hostId={pageState.meetingLink.user_id}
          onToggleTroubleshoot={handleTroubleshootToggle}
        />
      )}

      {/* Apply top padding when the banner is shown */}
      <div className={`${pageState.isHost ? 'pt-12' : ''}`}>
        {/* Always use Modern template now */}
        <TemplateLoader
          template={template}
          meetingLink={pageState.meetingLink}
          businessSlug={businessSlug || businessData.businessSlugValue}
          isDebugMeeting={slug === 'debug'}
          urlParams={urlParams}
          troubleshootMode={pageState.troubleshootMode}
        />
      </div>

      {pageState.isDebugMode && (slug === 'debug') && (
        <div className="container max-w-4xl mx-auto px-4 mt-8">
          <Alert>
            <AlertDescription className="text-center text-muted-foreground text-sm">
              {slug === 'debug' ? 
                "This is a debug meeting page shown because you're on the debug route." :
                "Debug mode is enabled. You may see additional diagnostic information."}
              {process.env.NODE_ENV === 'development' && " You can disable debug mode in the development settings."}
            </AlertDescription>
          </Alert>
        </div>
      )}
      </div>
    </BookingProvider>
  );
});

ExternalBookingPage.displayName = 'ExternalBookingPage';

export default ExternalBookingPage;
